import { encode, decode } from "./encoder.mjs";
import { splitCommas } from "./splitCommas.mjs";
import { isset, isStringable } from "./isset.mjs";
import { cleanDeep } from './cleanDeep.mjs';
import set from 'lodash/set.js';
import get from 'lodash/get.js';
import has from 'lodash/has.js';
import unset from 'lodash/unset.js';
import cloneDeep from 'lodash/cloneDeep.js';
import { getObjectPaths } from "./pathTree.mjs";
import { convert } from "./convert/index.mjs";
import { buildTypeDefs } from "./buildTypeDefs.mjs";
import { isEqual } from "./isEqual.mjs";
import defaults from 'lodash/defaults.js';
export class QueryString {
    static stringify(obj, options = {}) {
        let objToStringify = cloneDeep(obj);
        let topKeysToKeep = new Set();
        let topKeysToSkip = new Set();
        if (!!options.initialState) {
            let statePaths = getObjectPaths(options.initialState);
            for (let pathKey of statePaths) {
                if (pathKey.length < 1)
                    continue;
                if (topKeysToSkip.has(pathKey[0]))
                    continue;
                let curValue = get(objToStringify, pathKey, undefined);
                let initValue = get(options.initialState, pathKey);
                let hasInitialValue = (initValue !== null && typeof initValue !== 'undefined');
                let hasCurValue = (curValue !== null && typeof curValue !== 'undefined');
                if (pathKey.length === 1) {
                    if (isEqual(curValue, initValue)) {
                        unset(objToStringify, pathKey);
                    }
                    else if (hasInitialValue && !hasCurValue) {
                        set(objToStringify, pathKey, '');
                    }
                }
                else if (hasInitialValue && hasCurValue) {
                    let topCurValue = get(objToStringify, pathKey[0], undefined);
                    let topInitValue = get(options.initialState, pathKey[0]);
                    if (topCurValue && typeof topCurValue === 'object') {
                        topCurValue = JSON.parse(JSON.stringify(topCurValue, (_k, value) => {
                            return typeof value === 'undefined' || value === null ? undefined : value;
                        }));
                    }
                    if (topInitValue && typeof topInitValue === 'object') {
                        topInitValue = JSON.parse(JSON.stringify(topInitValue, (_k, value) => {
                            return typeof value === 'undefined' || value === null ? undefined : value;
                        }));
                    }
                    if (isEqual(topCurValue, topInitValue)) {
                        unset(objToStringify, pathKey[0]);
                        topKeysToSkip.add(pathKey[0]);
                    }
                }
                else if (hasInitialValue && !hasCurValue) {
                    set(objToStringify, pathKey, null);
                    topKeysToKeep.add(pathKey[0]);
                }
            }
        }
        objToStringify = cleanDeep(objToStringify, { emptyStrings: false });
        if (topKeysToKeep.size) {
            for (let key of topKeysToKeep) {
                if (!has(objToStringify, key)) {
                    set(objToStringify, key, '');
                }
            }
        }
        let seenObjects = [];
        function doStringify(obj, nested) {
            if (obj && typeof obj === 'object') {
                if (seenObjects.includes(obj))
                    return '';
                seenObjects.push(obj);
            }
            if (typeof obj === 'undefined' || obj === null) {
                return '';
            }
            else if (Array.isArray(obj)) {
                let arrResult = obj.filter(isset)
                    .map((a) => doStringify(a, true))
                    .join(',');
                return arrResult;
            }
            else if (typeof obj === 'object' && !!obj) {
                let keys = Object.keys(obj);
                keys = keys.filter(k => isset(obj[k]));
                let keyValueSep = '=', keySep = '&', prefix = '', suffix = '';
                if (nested) {
                    keyValueSep = ':';
                    keySep = ';';
                    prefix = '(';
                    suffix = ')';
                }
                let keyValues = keys.map(key => `${encode(key)}${keyValueSep}${doStringify(obj[key], true)}`);
                return `${prefix}${keyValues.join(keySep)}${suffix}`;
            }
            else if (typeof obj === 'function' || typeof obj === 'symbol') {
                return '';
            }
            else if (typeof obj === 'boolean') {
                return obj ? '1' : '0';
            }
            else if (typeof obj === 'number') {
                if (Number.isNaN(obj))
                    return '';
                return encode(obj.toString());
            }
            else if (isStringable(obj)) {
                return encode(obj.toString());
            }
            else {
                return encode(`${obj}`);
            }
        }
        return doStringify(objToStringify, false);
    }
    static parse(qs, options = {}) {
        qs = (qs ?? '').replace(/^\s*?\?/, '?');
        if (!qs || qs === '?')
            return options.initialState ?? {};
        if (qs[0] === '?')
            qs = qs.substr(1);
        let typeDefs = options.types;
        if (!typeDefs && !!options.initialState) {
            typeDefs = buildTypeDefs(options.initialState);
        }
        let result = {};
        function parseValue(value) {
            let valueArray = splitCommas(value);
            let parsedValues = valueArray.map(val => {
                if (val.length < 4) {
                    return decode(val);
                }
                if (val[0] === '(' && val.substr(-1, 1) === ')') {
                    let objValue = {};
                    let trimmed = val.substring(1, val.length - 1);
                    let allObjectKeys = splitCommas(trimmed, ';');
                    for (let objKeyValue of allObjectKeys) {
                        let [objK, objV] = splitCommas(objKeyValue, ':');
                        if (typeof objV === 'undefined') {
                            objValue[decode(objK)] = '';
                            continue;
                        }
                        objValue[decode(objK)] = parseValue(objV);
                    }
                    return objValue;
                }
                return decode(val);
            });
            if (valueArray.length < 1)
                return '';
            return (valueArray.length === 1)
                ? parsedValues[0]
                : parsedValues;
        }
        let allKeyValues = qs.split('&');
        for (let keyValue of allKeyValues) {
            let [key, value] = keyValue.split('=');
            if (typeof value === 'undefined') {
                result[decode(key)] = '';
                continue;
            }
            result[decode(key)] = parseValue(value);
        }
        if (typeof typeDefs !== 'undefined') {
            result = convert(result, {
                definedTuples: options.definedTuples ?? false,
                typeDef: typeDefs,
                typeDefsFromInitial: (!!typeDefs && !options.types && !options.lockTypesToInitialState),
                filterToTypeDef: options.filterToTypeDef ?? false,
            });
        }
        if (options.initialState)
            result = defaults(result, options.initialState);
        return result;
    }
    static merge(origQS, newValues, options = { deepMerge: false }) {
        let qsObject = QueryString.parse(origQS);
        if (options.deepMerge) {
            let newKeys = getObjectPaths(newValues);
            for (let key of newKeys) {
                let value = get(newValues, key);
                set(qsObject, key, value);
            }
        }
        else {
            let newKeys = Object.keys(newValues);
            for (let key of newKeys) {
                qsObject[key] = newValues[key];
            }
        }
        let qsOptions = {};
        if (options.initialState)
            qsOptions.initialState = options.initialState;
        return QueryString.stringify(qsObject, qsOptions);
    }
}
